// Pure helpers for the «Справочник» section (KOP-37 spec §5). No Vue, no axios.

import { toNumber, formatRub } from './format';

export { toNumber, formatRub };

export const str = (v) => (v === null || v === undefined ? '' : String(v));

const round2 = (n) => Math.round(n * 100) / 100;

export function priceOf(row) {
  const cp = row && row.current_price;
  if (!cp || typeof cp !== 'object') return null;
  return toNumber(cp.price);
}

export function isArchived(row) {
  return !!row && row.archived_at !== null && row.archived_at !== undefined;
}

export function lineTotal(unitPrice, quantity) {
  const p = toNumber(unitPrice);
  const q = toNumber(quantity);
  if (p === null || q === null) return null;
  return round2(p * q);
}

export function formatQuantityLine(consumable) {
  const c = consumable || {};
  const qty = toNumber(c.quantity) === null ? 0 : toNumber(c.quantity);
  return `${c.title || '—'} × ${qty} — ${formatRub(lineTotal(priceOf(c), qty))}`;
}

/** Spec §5.2. Drives both the table cells and the form tiles. */
export function workTypeMoney(workType) {
  const wt = workType || {};
  const list = Array.isArray(wt.consumables) ? wt.consumables : [];
  let consumablesTotal = 0;
  let incomplete = false;
  list.forEach((c) => {
    const p = priceOf(c);
    if (p === null) { incomplete = true; return; }
    const q = toNumber(c && c.quantity) || 0;
    consumablesTotal += p * q;
  });
  consumablesTotal = round2(consumablesTotal);
  const workPrice = priceOf(wt);
  const total = workPrice === null ? null : round2(consumablesTotal + workPrice);
  return { consumablesTotal, workPrice, total, incomplete };
}

/* ---------- consumable form (spec §5.3) ---------- */

export const TITLE_MAX = 255;

export function emptyConsumableForm() {
  return { title: '', price: '' };
}

export function consumableFormFromRow(row) {
  const p = priceOf(row);
  return { title: str(row && row.title), price: p === null ? '' : String(p) };
}

function titleError(title, emptyMsg, longMsg) {
  const t = str(title).trim();
  if (!t) return emptyMsg;
  if (t.length > TITLE_MAX) return longMsg;
  return null;
}

function validPrice(v) {
  const n = toNumber(v);
  return n !== null && n >= 0;
}

export function validateConsumableForm(form) {
  const f = form || {};
  const errors = [];
  const t = titleError(f.title, 'Укажите наименование.', 'Наименование длиннее 255 символов.');
  if (t) errors.push(t);
  if (!validPrice(f.price)) errors.push('Укажите стоимость (число не меньше 0).');
  return errors;
}

/** True when the entered price differs from the row's current price (null current price counts as different). */
export function priceChanged(formPrice, row) {
  return toNumber(formPrice) !== priceOf(row);
}

export function buildConsumablePayload(form, row) {
  const payload = { title: str(form.title).trim() };
  if (!row) {
    payload.price = toNumber(form.price);
    return payload;
  }
  payload.id = row.id;
  if (priceChanged(form.price, row)) payload.price = toNumber(form.price);
  return payload;
}

/* ---------- work-type form (spec §5.4) ---------- */

export function emptyWorkTypeForm() {
  return { title: '', price: '', items: [] };
}

export function itemFromConsumable(consumable, quantity = 1) {
  const c = consumable || {};
  return { id: c.id, title: str(c.title), archived: isArchived(c), unitPrice: priceOf(c), quantity: String(quantity) };
}

export function workTypeFormFromRow(row) {
  const r = row || {};
  const p = priceOf(r);
  const list = Array.isArray(r.consumables) ? r.consumables : [];
  return { title: str(r.title), price: p === null ? '' : String(p), items: list.map((c) => itemFromConsumable(c, c.quantity)) };
}

/** Active consumables not yet in the form — feeds the «— добавить материал —» select. */
export function availableConsumables(activeList, items) {
  const used = new Set((items || []).map((i) => i.id));
  return (Array.isArray(activeList) ? activeList : []).filter((c) => !isArchived(c) && !used.has(c.id));
}

/** Same math as the table, built from form state, so tiles and cells agree by construction. */
export function formMoney(form) {
  const f = form || {};
  return workTypeMoney({
    current_price: str(f.price).trim() === '' ? null : { price: f.price },
    consumables: (f.items || []).map((i) => ({
      quantity: toNumber(i.quantity) || 0,
      current_price: i.unitPrice === null || i.unitPrice === undefined ? null : { price: i.unitPrice },
    })),
  });
}

const isPositiveInt = (v) => /^\d+$/.test(str(v).trim()) && Number(v) > 0;

export function validateWorkTypeForm(form) {
  const f = form || {};
  const errors = [];
  const t = titleError(f.title, 'Укажите название работы.', 'Название длиннее 255 символов.');
  if (t) errors.push(t);
  if (!validPrice(f.price)) errors.push('Укажите стоимость работ (число не меньше 0).');
  if ((f.items || []).some((i) => !isPositiveInt(i.quantity))) errors.push('Количество материала должно быть целым числом больше 0.');
  return errors;
}

export function buildWorkTypePayload(form, row) {
  const payload = {
    title: str(form.title).trim(),
    consumables: (form.items || []).map((i) => ({ id: i.id, quantity: Number(i.quantity) })),
  };
  if (!row) {
    payload.price = toNumber(form.price);
    return payload;
  }
  payload.id = row.id;
  if (priceChanged(form.price, row)) payload.price = toNumber(form.price);
  return payload;
}
